import * as yup from 'yup';
import * as formSchemaUtils from './formSchemaUtils';
import { isLiquid } from '../utils';
import {
  HAZARD_STMT_OR_IS_NOT_HAZARDOUS_IS_REQUIRED,
  EUH_STMT_OR_IS_NOT_APPLICABLE,
  UN_NUMBER_OR_IS_REGULATED_IS_REQUIRED,
} from './formSchemaUtils';
import keycloak from '../keycloak';

export const getChemicalProductObjectSchema = () =>
  yup.object().shape({
    tradeName: yup
      .string()
      .max(200, formSchemaUtils.EXCEEDS_TRADE_NAME_SIZE)
      .when('selectedChemicalProduct.id', {
        is: -1,
        then: yup
          .mixed()
          .tradeNameValidation([yup.ref('id'), yup.ref('supplier'), yup.ref('purity')], keycloak)
          .mixedCustomRequired()
          .nullable()
          .required('Trade Name cannot be empty')
      })
      .nullable()
      .required('Trade Name cannot be empty'),
    chemicalName: yup.string().max(200, formSchemaUtils.EXCEEDS_CHEMICAL_NAME_SIZE).nullable(),
    state: yup.object({
      id: yup.string().required(formSchemaUtils.VALUE_IS_REQUIRED).nullable(),
    }),
    cmrClass: yup.string().max(200, formSchemaUtils.EXCEEDS_CMR_CATEGORY_SIZE).nullable(),
    supplier: yup
      .object({
        name: yup.string().max(200, formSchemaUtils.EXCEEDS_SUPPLIER_NAME_SIZE).nullable(),
      })
      .nullable(),
    purity: yup.string().max(200, formSchemaUtils.EXCEEDS_PURITY_SIZE).nullable(),
    remarks: yup.string().max(4000, formSchemaUtils.EXCEEDS_REMARKS_SIZE).nullable(),
    formula: yup.string().max(200, formSchemaUtils.EXCEEDS_FORMULA_SIZE).nullable(),
    casNumber: yup
      .object({
        casNumber: yup
          .mixed()
          .test('idCasNumber', formSchemaUtils.CAS_FORMAT_IS_NOT_VALID, v => {
            let patt = new RegExp(/^[0-9]{2,7}-[0-9]{2}-[0-9]{1}$/);
            return !v || patt.test(v);
          })
          .nullable(),
      })
      .nullable(),
    unNumber: yup
      .mixed()
      .requiredIfRefHasNoValue(yup.ref('isNotRegulated'), UN_NUMBER_OR_IS_REGULATED_IS_REQUIRED)
      .nullable(),
    isNotRegulated: yup
      .mixed()
      .requiredIfRefHasNoValue(yup.ref('unNumber'), formSchemaUtils.EXCLUDED_ERROR_MESSAGE)
      .nullable(),
    liquidCategory: yup.object().when('state', {
      is: state => isLiquid(state),
      then: formSchemaUtils.objectWithIdRequired(),
      otherwise: yup.object().nullable(),
    }),
    sdsFiles: yup
      .array()
      .min(1, formSchemaUtils.VALUE_IS_REQUIRED)
      .test(
        'sdsFiles',
        formSchemaUtils.VALUE_IS_REQUIRED,
        value =>
          value && value.length > 0 && value.some(f => !f.fileData.deleted && !f.fileData.delUser)
      )
      .required(formSchemaUtils.VALUE_IS_REQUIRED)
      .of(
        yup.object().shape({
          format: yup
            .mixed()
            .test('format', formSchemaUtils.UNSUPPORTED_FILE_FORMAT, function (v) {
              return (
                this.parent &&
                (this.parent.fileData.id ||
                  (this.parent.fileData.format &&
                    formSchemaUtils.SDS_FILE_SUPPORTED_FORMATS.includes(
                      this.parent.fileData.format
                    )))
              );
            })
            .nullable(),
          size: yup
            .mixed()
            .test('size', formSchemaUtils.EXCEEDS_FILE_SIZE_LIMIT, function (v) {
              return (
                this.parent &&
                (this.parent.fileData.id ||
                  !this.parent.fileData.file ||
                  this.parent.fileData.file.size <= formSchemaUtils.FILE_SIZE_LIMIT)
              );
            })
            .nullable(),
          revisionDte: yup
            .mixed()
            .test('revisionDte', formSchemaUtils.VALUE_IS_REQUIRED, function (v) {
              return (
                this.parent && (this.parent.fileData.deleted || this.parent.fileData.delUser || v)
              );
            })
            .nullable(),
        })
      )
      .nullable(),
    hazardStatements: yup
      .mixed()
      .requiredIfRefHasNoValue(
        yup.ref('isNotHazardous'),
        HAZARD_STMT_OR_IS_NOT_HAZARDOUS_IS_REQUIRED
      )
      .nullable(),
    isNotHazardous: yup.mixed().when('hazardStatements', {
      is: value => value && value.length > 0,
      then: yup
        .mixed()
        .test('isNotHazardous', formSchemaUtils.CANNOT_BE_DECLARED_NOT_HAZARDOUS, v => !v)
        .nullable(),
      otherwise: yup
        .mixed()
        .requiredIfRefHasNoValue(
          yup.ref('hazardStatements'),
          formSchemaUtils.EXCLUDED_ERROR_MESSAGE
        )
        .nullable(),
    }),
    euhStatements: yup
      .mixed()
      .requiredIfRefHasNoValue(
        yup.ref('isEuhNotApplicable'),
        EUH_STMT_OR_IS_NOT_APPLICABLE
      )
      .nullable(),
      isEuhNotApplicable: yup.mixed().when('euhStatements', {
      is: value => value && value.length > 0,
      then: yup
        .mixed()
        .test('isEuhNotApplicable', formSchemaUtils.CANNOT_HAVE_EUH_STATEMENT, v => !v)
        .nullable(),
      otherwise: yup
        .mixed()
        .requiredIfRefHasNoValue(
          yup.ref('euhStatements'),
          formSchemaUtils.EXCLUDED_ERROR_MESSAGE
        )
        .nullable(),
    }),
    validateUser: yup.object().requiredIfRefHasValue(yup.ref('validateDte')).nullable(),
    validateDte: yup
      .object()
      .requiredIfRefHasValue(yup.ref('validateUser'))
      .isSameOrBeforeToday()
      .nullable(),
  });

const getChemicalProductDetailsFormSchema = () =>
  yup.object().shape({
    chemicalProduct: getChemicalProductObjectSchema(),
  });

export default getChemicalProductDetailsFormSchema;
